// src/pages/FormListsLabPage.jsx

import React, {
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';

import GroupedSpreadsheetCanvas from '../components/Spreadsheet/GroupedSpreadsheetCanvas';
import SpreadsheetWorkspaceSidePanel from '../components/Spreadsheet/SpreadsheetWorkspaceSidePanel';
import FormListsDependencyCell from '../components/FormLists/FormListsDependencyCell';
import FormListsGroupHeaderContent from '../components/FormLists/FormListsGroupHeaderContent';
import FormListsLabSidePanelList from '../components/FormLists/FormListsLabSidePanelList';
import FieldSettings, {
  FIELD_SETTINGS_ACCESS_MODE,
} from '../components/FormSettings/FormDesignLayout/FieldSettings';

import {
  buildFormListsGridDefaults,
} from '../components/FormLists/formListsGridConfig';

import {
  buildFormListGroups,
  ensureFormListOptionRowIdentity,
} from '../components/FormLists/formListsAdapters';

import {
  isFormListsValueCellReadOnly,
} from '../components/FormLists/formListsGovernanceUtils';

import useFormListsSpreadsheetSync from '../components/FormLists/useFormListsSpreadsheetSync';
import { useFormListsDraftSync } from '../contexts/FormListsDraftSyncContext';

import {
  getGroupId,
  normalizeString,
} from '../components/Spreadsheet/groupedSpreadsheetUtils';

import {
  buildFormListsLabGroupOrderStorageKey,
  buildFormListsLabPanelStateStorageKey,
  normalizeGroupOrder,
  orderGroupsByGroupOrder,
} from '../components/FormLists/formListsLabGroupOrderUtils';

import {
  safeGetItem,
  safeSetItem,
} from '../utils/storageUtils';

import { useFieldMetadata } from '../contexts/FieldMetadataContext';
import { useMultiAnswerField } from '../contexts/MultiAnswerFieldContext';
import { useDropdownDependencies } from '../contexts/DropdownDependenciesContext';
import { useFieldEdit } from '../contexts/FieldEditContext';

/**
 * FormListsLabPage
 * -----------------------------------------------------------------------------
 * Experimental V2 Form Lists page mounted under the existing form-builder route.
 *
 * PURPOSE
 *  • Test the shared spreadsheet engine with REAL Form Lists context data
 *  • Keep the current /lists route untouched while V2 is validated
 *  • Preserve the existing form-local dropdown schema
 *  • Preserve the existing dependency side-panel workflow
 *  • Apply only dirty groups to MultiAnswerFieldContext
 *
 * IMPORTANT
 *  • Do NOT wrap this page in FormLayoutProvider, FieldEditProvider, or DndProvider.
 *    FormBuilderWrapper already provides the form-builder context stack.
 *  • This page does NOT write Firestore directly.
 *  • This page does NOT publish or save drafts directly.
 *  • Apply writes only to existing form-local context/storage paths.
 *
 * CURRENT V2 SCOPE
 *  • Renders dropdown groups from:
 *      fieldsMetadata + dropdownOptions
 *  • Uses required columns:
 *      Option / Hint / Dep
 *  • Applies dirty option groups through:
 *      saveDropdownOptionsToStorage(optionsSetUFI, nextOptions)
 *  • Opens dependency settings through the existing FieldSettings side panel.
 * -----------------------------------------------------------------------------
 */

const snackbarInitialState = {
  open: false,
  severity: 'success',
  message: '',
};

/**
 * FormListsLabPage spreadsheet canvas layout
 * ---------------------------------------------------------------------------
 * Page-level layout override for the shared spreadsheet canvas.
 *
 * IMPORTANT
 *  • This is NOT spreadsheet behavior.
 *  • The shared Spreadsheet/ components still own editing, keyboard handling,
 *    dirty tracking, selection, snapshots, focus, scroll, and rendering.
 *  • This object only tells the reusable canvas how to fit inside this page's
 *    flex layout.
 *
 * WHY THIS IS MODULE-LEVEL
 *  • Passing an inline sx object creates a new object reference on every page
 *    render.
 *  • GroupedSpreadsheetCanvas is memoized, so a stable sx reference avoids
 *    unnecessary prop churn while preserving the exact same styling.
 */
const spreadsheetCanvasSx = {
  flex: '1 1 auto',
  width: '100%',
  height: 'auto',
  minHeight: 0,
  bgcolor: 'background.paper',
  borderLeft: 0,
  borderRight: 0,
  borderBottom: 0,
};

/**
 * FormListsLab workspace side-panel sizing
 * ---------------------------------------------------------------------------
 * These values are intentionally page-level. The reusable
 * SpreadsheetWorkspaceSidePanel only owns the shell behavior; the page decides
 * how much workspace real estate it wants to allocate.
 *
 * IMPORTANT
 *  • Expanded width matches the existing form-builder SidePanel pattern.
 *  • Collapsed width matches the SmartLists mini-rail pattern.
 */
const FORM_LISTS_LAB_PANEL_EXPANDED_WIDTH = '30%';
const FORM_LISTS_LAB_PANEL_COLLAPSED_WIDTH = 56;

/**
 * FormListsLab column-width persistence
 * ---------------------------------------------------------------------------
 * Column widths are workspace UI preferences, not form content.
 *
 * WHY THIS IS PAGE-LEVEL
 *  • GroupedSpreadsheetCanvas must remain reusable and persistence-agnostic.
 *  • Form Lists adapters should map form/list data, not own workspace chrome.
 *  • FormListsLabPage already owns UI-only preferences such as:
 *      - side-panel collapse state
 *      - spreadsheet group order
 *
 * IMPORTANT
 *  • These widths should survive refresh/remount for this form workspace.
 *  • These widths should NOT be written into save/publish form payloads.
 *  • A future cross-device version can promote this storage boundary to a
 *    user/org-scoped Firestore preferences document without changing the
 *    shared Spreadsheet/ engine.
 */
const FORM_LISTS_LAB_COLUMN_WIDTHS_SAVE_DEBOUNCE_MS = 160;

const buildFormListsLabColumnWidthsStorageKey = (formId) => (
  `form_${normalizeString(formId, 'unknown')}_formListsLab_columnWidths`
);

const isColumnWidthsMap = (value) => (
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value)
);

const areColumnWidthMapsEqual = (a = {}, b = {}) => (
  JSON.stringify(a || {}) === JSON.stringify(b || {})
);

/**
 * hydrateFormListsLabColumnWidthsFromStorage
 * ---------------------------------------------------------------------------
 * Reads persisted FormListsLab column widths from local/session storage.
 *
 * WHY THIS EXISTS
 *  • GroupedSpreadsheetCanvas treats initialColumnWidths as initial local state.
 *  • Therefore, the page should hydrate workspace widths before mounting the
 *    Canvas instead of mounting with defaults and trying to correct later.
 *
 * IMPORTANT
 *  • This is local workspace UI state only.
 *  • It does not affect Save Draft / Publish form payloads.
 *  • It intentionally uses the same storage pattern as other FormListsLab
 *    workspace preferences.
 */
const hydrateFormListsLabColumnWidthsFromStorage = (formId) => {
  const safeFormId = normalizeString(formId, '');

  if (!safeFormId) {
    return {};
  }

  const storageKey = buildFormListsLabColumnWidthsStorageKey(safeFormId);
  const savedColumnWidths = safeGetItem(storageKey);

  return isColumnWidthsMap(savedColumnWidths)
    ? savedColumnWidths
    : {};
};

const FormListsLabPage = () => {
  const canvasRef = useRef(null);
  const columnWidthsSaveTimeoutRef = useRef(null);
  const latestColumnWidthsRef = useRef({});

  const navigate = useNavigate();
  const { formId: urlFormId } = useParams();

  const {
    fieldsMetadata = {},
    isLoading: fieldsMetadataLoading,
    error: fieldsMetadataError,
    updateFieldMetadata,
  } = useFieldMetadata() || {};

  const {
    dropdownOptions = {},
    loading: dropdownOptionsLoading,
    error: dropdownOptionsError,
    saveDropdownOptionsToStorage,
  } = useMultiAnswerField() || {};

  const {
    loading: dropdownDependenciesLoading,
    dependencyFlags = {},
    getDependenciesForOption,
  } = useDropdownDependencies() || {};

  const {
    selectFieldForEdit,
    selectFieldForEditImmediate,
    selectedFieldForEdit,
    clearSelectedField,
    updateField,
  } = useFieldEdit() || {};

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [initialTab, setInitialTab] = useState('basic');
  const [fieldDependencyData, setFieldDependencyData] = useState({});
  const [dependencyDropdownOptionsOverride, setDependencyDropdownOptionsOverride] =
    useState(null);
  const [settingsAccessMode, setSettingsAccessMode] = useState(
    FIELD_SETTINGS_ACCESS_MODE.FULL,
  );
  const [pendingSettingsRequest, setPendingSettingsRequest] = useState(null);

  /**
   * Workspace side-panel state
   * ---------------------------------------------------------------------------
   * This is local UI state for the FormListsLab workspace.
   *
   * IMPORTANT
   *  • It does not modify field metadata.
   *  • It does not modify dropdownOptions.
   *  • It does not modify dropdownDependencies.
   *  • It only controls the lab workspace presentation.
   */
  const [workspacePanelCollapsed, setWorkspacePanelCollapsed] = useState(false);
  const [workspacePanelMode, setWorkspacePanelMode] = useState('list');
  const [selectedWorkspaceGroupId, setSelectedWorkspaceGroupId] = useState('');
  const [formListGroupOrder, setFormListGroupOrder] = useState([]);

  /**
   * Persisted spreadsheet column widths
   * ---------------------------------------------------------------------------
   * Hydrated synchronously on first mount so the Canvas receives the correct
   * initialColumnWidths before it creates internal local width state.
   *
   * IMPORTANT
   *  • This avoids a default-width first mount.
   *  • This does not make the Canvas controlled.
   *  • The Canvas still owns live resize state while mounted.
   */
  const [persistedColumnWidths, setPersistedColumnWidths] = useState(() => (
    hydrateFormListsLabColumnWidthsFromStorage(urlFormId)
  ));

  const [hasHydratedColumnWidths, setHasHydratedColumnWidths] = useState(true);

  const [snackbar, setSnackbar] = useState(snackbarInitialState);

const showSnackbar = useCallback((severity, message) => {
  setSnackbar({
    open: true,
    severity,
    message,
  });
}, []);

const handleCloseSnackbar = useCallback(() => {
  setSnackbar((prev) => ({
    ...prev,
    open: false,
  }));
}, []);

  const groupOrderStorageKey = useMemo(() => (
    buildFormListsLabGroupOrderStorageKey(urlFormId)
  ), [urlFormId]);

  const panelStateStorageKey = useMemo(() => (
    buildFormListsLabPanelStateStorageKey(urlFormId)
  ), [urlFormId]);

  const columnWidthsStorageKey = useMemo(() => (
    buildFormListsLabColumnWidthsStorageKey(urlFormId)
  ), [urlFormId]);

  const gridDefaults = useMemo(() => buildFormListsGridDefaults(), []);

  /**
   * Initial column widths passed into the shared spreadsheet canvas.
   *
   * IMPORTANT
   *  • Adapter defaults provide the baseline Form Lists dimensions.
   *  • Persisted workspace widths override defaults.
   *  • The shared Canvas still owns live resize behavior while mounted.
   */
  const initialFormListColumnWidths = useMemo(() => ({
    ...(gridDefaults.columnWidths || {}),
    ...(persistedColumnWidths || {}),
  }), [
    gridDefaults.columnWidths,
    persistedColumnWidths,
  ]);

  const baseFormListGroups = useMemo(() => {
    return buildFormListGroups({
      fieldsMetadata,
      dropdownOptions,
      includeOrgManaged: true,
    });
  }, [
    fieldsMetadata,
    dropdownOptions,
  ]);

  const {
    registerFormListsFlush,
    getFormListsUnsavedWorkingCopyStatus,
    markFormListsUnsavedWorkingCopy,
  } = useFormListsDraftSync() || {};

  const {
    dirtySummary,
    effectiveDropdownOptions,
    handleDirtyStateChange,
    handleCellCommit,
    handleCommitBoundary,
    handleApplyChanges,
    flushDirtyChanges,
    refreshDraftOverridesFromCanvas,
  } = useFormListsSpreadsheetSync({
    formId: urlFormId,
    canvasRef,
    baseGroups: baseFormListGroups,
    baseDropdownOptions: dropdownOptions,
    saveDropdownOptionsToStorage,
    updateFieldMetadata,
    updateField,
    registerFormListsFlush,
    markFormListsUnsavedWorkingCopy,
    showSnackbar,
  });

  /**
   * Latest dirty summary + flush refs
   * ---------------------------------------------------------------------------
   * Route-exit cleanup must read the latest dirty summary and latest flush
   * callback without making the cleanup effect depend on every dirty-state or
   * hook identity change.
   *
   * WHY
   *  • flushDirtyChanges can receive a new function identity when adapter inputs
   *    change.
   *  • A cleanup effect that depends directly on flushDirtyChanges could run
   *    during normal re-render/reconciliation, not only when leaving the page.
   *  • Refs let route-exit cleanup remain stable while still calling the newest
   *    flush function.
   */
  const latestDirtySummaryRef = useRef(dirtySummary);
  const latestFlushDirtyChangesRef = useRef(flushDirtyChanges);

  useEffect(() => {
    latestDirtySummaryRef.current = dirtySummary;
  }, [
    dirtySummary,
  ]);

  useEffect(() => {
    latestFlushDirtyChangesRef.current = flushDirtyChanges;
  }, [
    flushDirtyChanges,
  ]);

  const formListGroups = useMemo(() => {
    return buildFormListGroups({
      fieldsMetadata,
      dropdownOptions: effectiveDropdownOptions,
      includeOrgManaged: true,
    });
  }, [
    fieldsMetadata,
    effectiveDropdownOptions,
  ]);

  /**
   * Route-exit Form Lists fallback flush
   * ---------------------------------------------------------------------------
   * When the user leaves Lists through a non-toolbar path, preserve spreadsheet
   * dropdown edits by promoting dirty option sets into builder-local form state.
   *
   * PRIMARY CONTROLLED PATH
   *  • FormBuilderToolbar owns controlled builder-step navigation and should
   *    call flushFormListsDraftChanges(...) before navigating away from Lists.
   *
   * FALLBACK PATH
   *  • This layout cleanup protects exits that do not go through the toolbar:
   *      - browser back
   *      - navbar Home / Back
   *      - programmatic route changes
   *      - future links that bypass the toolbar
   *
   * IMPORTANT
   *  • This does NOT save to Firestore.
   *  • This does NOT publish.
   *  • This does NOT run on every keystroke.
   *  • This flushes dirty option sets only.
   *  • The persistent unsaved-working-copy status keeps the dirty badge visible
   *    when the user returns to Lists before Save Draft / Publish.
   *
   * WHY useLayoutEffect
   *  • Route-exit cleanup needs the best chance to read canvasRef.current before
   *    child refs are detached during unmount.
   *  • Passive useEffect cleanup can be too late for ref-sensitive fallback
   *    work.
   *  • The latest dirty summary and flush callback are still read from refs so
   *    this cleanup does not depend on normal dirty-state or hook identity
   *    changes.
   */
  useLayoutEffect(() => {
    return () => {
      const latestDirtySummary = latestDirtySummaryRef.current || {};
      const flushOnExit = latestFlushDirtyChangesRef.current;

      if (typeof flushOnExit !== 'function') {
        console.warn(
          '[FormListsLabPage] Unable to flush List changes on route exit ' +
            'because flushDirtyChanges is unavailable.',
          {
            formId: urlFormId,
            latestDirtySummary,
            hasCanvasRef: !!canvasRef.current,
            hasFlushPendingEdit:
              typeof canvasRef.current?.flushPendingEdit === 'function',
          }
        );

        return;
      }

      /**
       * Route-exit fallback intentionally runs even when dirtySummary is 0.
       *
       * WHY
       *  • The active edit cell may not have committed yet.
       *  • flushDirtyChanges(...) owns the deterministic boundary:
       *      1. ask the canvas to flush the active edit
       *      2. build the dirty snapshot
       *      3. apply dirty option writes only
       *      4. no-op if nothing changed
       *  • Checking dirtySummary before flushDirtyChanges can skip the exact
       *    active-edit case this fallback is meant to protect.
       *
       * PERFORMANCE
       *  • This runs only when leaving Form Lists, not on every keystroke.
       *  • The no-op path is cheap.
       */
      void Promise.resolve(
        flushOnExit({
          reason: 'form_lists_route_exit',
          silent: true,
          clearDirtyOnSuccess: false,
          force: true,
          publishDraftOverridesToReactState: false,
        })
      ).catch((error) => {
        console.error(
          '[FormListsLabPage] Route-exit List flush failed:',
          {
            formId: urlFormId,
            latestDirtySummary,
            error,
          }
        );
      });
    };
  }, [
    urlFormId,
  ]);

  /**
   * Hydrate persisted FormListsLab workspace group order.
   *
   * IMPORTANT
   *  • This is a UI preference only.
   *  • It does not change field metadata order.
   *  • It does not change dropdown option order.
   *  • New groups are appended automatically.
   */
  useEffect(() => {
    if (!urlFormId) return;

    const savedGroupOrder = safeGetItem(groupOrderStorageKey);
    const nextGroupOrder = normalizeGroupOrder({
      groups: formListGroups,
      groupOrder: Array.isArray(savedGroupOrder) ? savedGroupOrder : [],
    });

    setFormListGroupOrder((prev) => {
      const prevSignature = JSON.stringify(prev || []);
      const nextSignature = JSON.stringify(nextGroupOrder || []);

      return prevSignature === nextSignature ? prev : nextGroupOrder;
    });
  }, [
    formListGroups,
    groupOrderStorageKey,
    urlFormId,
  ]);

  /**
   * Persist FormListsLab workspace group order.
   */
  useEffect(() => {
    if (!urlFormId) return;
    if (!Array.isArray(formListGroupOrder) || formListGroupOrder.length === 0) return;

    safeSetItem(groupOrderStorageKey, formListGroupOrder);
  }, [
    formListGroupOrder,
    groupOrderStorageKey,
    urlFormId,
  ]);

  /**
   * Hydrate/persist side-panel collapsed state.
   */
  useEffect(() => {
    if (!urlFormId) return;

    const savedPanelState = safeGetItem(panelStateStorageKey);

    if (
      savedPanelState &&
      typeof savedPanelState === 'object' &&
      typeof savedPanelState.collapsed === 'boolean'
    ) {
      setWorkspacePanelCollapsed(savedPanelState.collapsed);
    }
  }, [
    panelStateStorageKey,
    urlFormId,
  ]);

  useEffect(() => {
    if (!urlFormId) return;

    safeSetItem(panelStateStorageKey, {
      collapsed: workspacePanelCollapsed,
    });
  }, [
    panelStateStorageKey,
    urlFormId,
    workspacePanelCollapsed,
  ]);

  /**
   * Hydrate persisted FormListsLab spreadsheet column widths.
   *
   * IMPORTANT
   *  • This is a workspace UI preference only.
   *  • It does not change field metadata.
   *  • It does not change dropdownOptions.
   *  • It does not change dropdownDependencies.
   *  • It does not participate in Save Draft / Publish payloads.
   *
   * ENTERPRISE READINESS RULE
   *  • GroupedSpreadsheetCanvas treats initialColumnWidths as initial local
   *    state.
   *  • When formId changes, we briefly mark widths as not hydrated so the next
   *    Canvas mount cannot receive another form's workspace widths.
   *  • On initial page load, persistedColumnWidths is already hydrated through
   *    the useState initializer above, so this does not add visible lag.
   */
  useEffect(() => {
    if (!urlFormId) {
      latestColumnWidthsRef.current = {};
      setPersistedColumnWidths({});
      setHasHydratedColumnWidths(true);
      return;
    }

    setHasHydratedColumnWidths(false);

    const nextColumnWidths =
      hydrateFormListsLabColumnWidthsFromStorage(urlFormId);

    latestColumnWidthsRef.current = nextColumnWidths;

    setPersistedColumnWidths((prev) => (
      areColumnWidthMapsEqual(prev, nextColumnWidths)
        ? prev
        : nextColumnWidths
    ));

    setHasHydratedColumnWidths(true);
  }, [
    urlFormId,
  ]);

  /**
   * Cleanup pending column-width persistence work.
   *
   * IMPORTANT
   *  • If the user navigates away shortly after resizing, do not drop the
   *    latest local workspace preference just because the debounce did not fire.
   *  • This does not write form content.
   *  • This only flushes the latest local spreadsheet width map.
   */
  useEffect(() => {
    return () => {
      if (columnWidthsSaveTimeoutRef.current) {
        window.clearTimeout(columnWidthsSaveTimeoutRef.current);
        columnWidthsSaveTimeoutRef.current = null;

        const widthsToPersist = latestColumnWidthsRef.current || {};

        if (urlFormId && isColumnWidthsMap(widthsToPersist)) {
          safeSetItem(columnWidthsStorageKey, widthsToPersist);
        }
      }
    };
  }, [
    columnWidthsStorageKey,
    urlFormId,
  ]);

  /**
   * Persist FormListsLab spreadsheet column widths.
   *
   * ENTERPRISE PERFORMANCE RULE
   *  • The shared Canvas can emit width changes at resize-frame cadence.
   *  • This page should not re-render or write storage on every pointer frame.
   *  • We keep the latest width map in a ref immediately, then debounce only the
   *    durable storage write.
   *
   * IMPORTANT
   *  • Ignore emissions until the page has hydrated the correct form's
   *    workspace widths.
   *  • This prevents default Canvas widths from overwriting saved widths during
   *    page boot or formId transitions.
   *  • Do not call setPersistedColumnWidths here. While the Canvas is mounted,
   *    it already owns live resize state locally; updating page React state on
   *    each debounced save creates unnecessary parent re-renders.
   */
  const handleColumnWidthsChange = useCallback((nextColumnWidths = {}) => {
    if (!urlFormId) return;
    if (!hasHydratedColumnWidths) return;
    if (!isColumnWidthsMap(nextColumnWidths)) return;

    latestColumnWidthsRef.current = nextColumnWidths;

    if (columnWidthsSaveTimeoutRef.current) {
      window.clearTimeout(columnWidthsSaveTimeoutRef.current);
    }

    columnWidthsSaveTimeoutRef.current = window.setTimeout(() => {
      columnWidthsSaveTimeoutRef.current = null;

      const widthsToPersist = latestColumnWidthsRef.current || {};

      safeSetItem(columnWidthsStorageKey, widthsToPersist);
    }, FORM_LISTS_LAB_COLUMN_WIDTHS_SAVE_DEBOUNCE_MS);
  }, [
    columnWidthsStorageKey,
    hasHydratedColumnWidths,
    urlFormId,
  ]);

  /**
   * Workspace group ordering
   * ---------------------------------------------------------------------------
   * The sidepanel should reorder immediately when the user drops a field.
   *
   * The spreadsheet canvas is heavier than the sidepanel because it renders the
   * full grouped sheet. To keep drag/drop feeling like SmartLists, we use the
   * immediate order for the sidepanel and a deferred order for the canvas.
   *
   * IMPORTANT
   *  • This does not change save/publish data.
   *  • This does not change dropdownOptions.
   *  • This does not change dependencies.
   *  • This is only a workspace ordering preference.
   */
  const orderedPanelFormListGroups = useMemo(() => (
    orderGroupsByGroupOrder({
      groups: formListGroups,
      groupOrder: formListGroupOrder,
    })
  ), [
    formListGroups,
    formListGroupOrder,
  ]);

  const deferredFormListGroupOrder = useDeferredValue(formListGroupOrder);

  const orderedCanvasFormListGroups = useMemo(() => (
    orderGroupsByGroupOrder({
      groups: formListGroups,
      groupOrder: deferredFormListGroupOrder,
    })
  ), [
    formListGroups,
    deferredFormListGroupOrder,
  ]);

  const isLoading = Boolean(
    fieldsMetadataLoading ||
      dropdownOptionsLoading ||
      dropdownDependenciesLoading,
  );

  const hasNoFormListGroups =
    !isLoading && orderedPanelFormListGroups.length === 0;

  const loadError =
    fieldsMetadataError ||
    dropdownOptionsError ||
    null;

  const handleCloseSettings = useCallback(() => {
    setIsSettingsOpen(false);
    setSelectedFieldId(null);
    setInitialTab('basic');
    setFieldDependencyData({});
    setDependencyDropdownOptionsOverride(null);
    setSettingsAccessMode(FIELD_SETTINGS_ACCESS_MODE.FULL);
    setPendingSettingsRequest(null);
    setWorkspacePanelMode('list');

    if (typeof clearSelectedField === 'function') {
      clearSelectedField();
    }
  }, [clearSelectedField]);

  const handleOpenFieldSettings = useCallback(async ({
    fieldId,
    initialTab: nextInitialTab = 'basic',
    fieldDependencyData: nextFieldDependencyData = {},
    settingsAccessMode: nextSettingsAccessMode = FIELD_SETTINGS_ACCESS_MODE.FULL,
  }) => {
    const safeFieldId = normalizeString(fieldId, '');

    if (!safeFieldId) {
      console.error(
        '[FormListsLabPage] Cannot open field settings: missing fieldId.',
      );
      return;
    }

    const fieldMetadata = fieldsMetadata?.[safeFieldId];

    if (!fieldMetadata || fieldsMetadataLoading) {
      console.warn(
        '[FormListsLabPage] Field metadata is not ready. Storing ' +
          'pending settings request.',
        {
          fieldId: safeFieldId,
          initialTab: nextInitialTab,
        },
      );

      setPendingSettingsRequest({
        fieldId: safeFieldId,
        initialTab: nextInitialTab,
        fieldDependencyData: nextFieldDependencyData,
        settingsAccessMode: nextSettingsAccessMode,
      });

      return;
    }

    if (dropdownDependenciesLoading) {
      console.warn(
        '[FormListsLabPage] Dropdown dependencies are still loading. ' +
          'Storing pending settings request.',
        {
          fieldId: safeFieldId,
          initialTab: nextInitialTab,
        },
      );

      setPendingSettingsRequest({
        fieldId: safeFieldId,
        initialTab: nextInitialTab,
        fieldDependencyData: nextFieldDependencyData,
        settingsAccessMode: nextSettingsAccessMode,
      });

      return;
    }

    setSelectedFieldId(safeFieldId);
    setInitialTab(nextInitialTab);
    setFieldDependencyData(nextFieldDependencyData || {});

    if (
      nextSettingsAccessMode !== FIELD_SETTINGS_ACCESS_MODE.DEPENDENCIES_ONLY
    ) {
      setDependencyDropdownOptionsOverride(null);
    }

    setSettingsAccessMode(nextSettingsAccessMode);
    setIsSettingsOpen(true);
    setWorkspacePanelMode('settings');
    setWorkspacePanelCollapsed(false);

    const selectForSettings =
      typeof selectFieldForEditImmediate === 'function'
        ? selectFieldForEditImmediate
        : selectFieldForEdit;

    if (typeof selectForSettings === 'function') {
      try {
        await selectForSettings(safeFieldId);
      } catch (error) {
        console.error(
          '[FormListsLabPage] selectFieldForEdit failed:',
          error,
        );
      }
    }
  }, [
    fieldsMetadata,
    fieldsMetadataLoading,
    dropdownDependenciesLoading,
    selectFieldForEdit,
    selectFieldForEditImmediate,
  ]);

  useEffect(() => {
    if (!pendingSettingsRequest) return;
    if (fieldsMetadataLoading || dropdownDependenciesLoading) return;

    const pending = pendingSettingsRequest;
    setPendingSettingsRequest(null);

    handleOpenFieldSettings({
      fieldId: pending.fieldId,
      initialTab: pending.initialTab,
      fieldDependencyData: pending.fieldDependencyData,
      settingsAccessMode:
        pending.settingsAccessMode || FIELD_SETTINGS_ACCESS_MODE.FULL,
    });
  }, [
    pendingSettingsRequest,
    fieldsMetadataLoading,
    dropdownDependenciesLoading,
    handleOpenFieldSettings,
  ]);

  useEffect(() => {
    if (selectedFieldForEdit && selectedFieldId) {
      setIsSettingsOpen(true);
      return;
    }

    if (!selectedFieldForEdit && !pendingSettingsRequest) {
      setIsSettingsOpen(false);
    }
  }, [
    selectedFieldForEdit,
    selectedFieldId,
    pendingSettingsRequest,
  ]);

  const handleOpenDependencySettings = useCallback(({
    fieldId,
    optionsSetUFI,
    optionId,
  }) => {
    const safeFieldId = normalizeString(fieldId, '');
    const safeOptionsSetUFI = normalizeString(optionsSetUFI, '');
    const safeOptionId = normalizeString(optionId, '');

    if (!safeFieldId || !safeOptionsSetUFI || !safeOptionId) {
      console.error(
        '[FormListsLabPage] Missing fieldId, optionsSetUFI, or optionId ' +
          'for dependency settings.',
        {
          fieldId,
          optionsSetUFI,
          optionId,
        },
      );
      return;
    }

    /**
     * Dependency-panel freshness boundary
     * -------------------------------------------------------------------------
     * Before opening DependencyManager, pull the latest committed spreadsheet
     * option rows from the canvas into a dependency-panel-only override.
     *
     * WHY
     *  • FormListsLab keeps normal spreadsheet edits local for speed.
     *  • DependencyManager must still see the latest committed option labels and
     *    optionIds immediately.
     *  • This avoids writing MultiAnswerFieldContext/storage after every
     *    Enter/Tab while preserving the existing dependency side-panel UX.
     */
    const draftRefreshResult =
      typeof refreshDraftOverridesFromCanvas === 'function'
        ? refreshDraftOverridesFromCanvas({
            reason: 'open_dependency_settings',
            publishToReactState: false,
          })
        : null;

    setDependencyDropdownOptionsOverride(
      draftRefreshResult?.dropdownOptionsOverride || null,
    );

    const onDependenciesUpdated = () => {
      console.log(
        '[FormListsLabPage] Dependencies updated for option:',
        {
          fieldId: safeFieldId,
          optionsSetUFI: safeOptionsSetUFI,
          optionId: safeOptionId,
        },
      );
    };

    handleOpenFieldSettings({
      fieldId: safeFieldId,
      initialTab: 'dependencies',
      settingsAccessMode: FIELD_SETTINGS_ACCESS_MODE.DEPENDENCIES_ONLY,
      fieldDependencyData: {
        optionId: safeOptionId,
        optionsSetUFI: safeOptionsSetUFI,
        onDependenciesUpdated,
      },
    });
  }, [
    handleOpenFieldSettings,
    refreshDraftOverridesFromCanvas,
  ]);

  const handleFormListsCommitBoundary = useCallback((commitEvent = {}) => {
    const draftRefreshResult = handleCommitBoundary(commitEvent);

    /**
     * Dependency panel live-refresh
     * -------------------------------------------------------------------------
     * If the dependency side panel is already open and the user commits another
     * spreadsheet edit, refresh only the dependency-panel override.
     *
     * IMPORTANT
     *  • This does NOT write MultiAnswerFieldContext.
     *  • This does NOT write storage.
     *  • This does NOT publish draft overrides into FormListsLab's main groups.
     *  • This keeps the dependency panel fresh without slowing normal sheet
     *    navigation when the panel is not in dependency mode.
     */
    if (
      isSettingsOpen &&
      settingsAccessMode === FIELD_SETTINGS_ACCESS_MODE.DEPENDENCIES_ONLY
    ) {
      setDependencyDropdownOptionsOverride(
        draftRefreshResult?.dropdownOptionsOverride || null,
      );
    }

    return draftRefreshResult;
  }, [
    handleCommitBoundary,
    isSettingsOpen,
    settingsAccessMode,
  ]);

  const renderCustomCell = useCallback((cell) => {
    if (cell?.columnKey !== 'dependency') {
      return null;
    }

    const group = orderedCanvasFormListGroups.find(
      (candidate) => candidate.groupId === cell.groupId,
    );

    if (!group) {
      return null;
    }

    return (
      <FormListsDependencyCell
        group={group}
        groupId={cell.groupId}
        row={cell.row}
        rowId={cell.rowId}
        rowIndex={cell.rowIndex}
        selected={cell.selected}
        readOnly={cell.readOnly}
        disabled={cell.disabled}
        dependencyFlags={dependencyFlags}
        getDependenciesForOption={getDependenciesForOption}
        onOpenDependencySettings={handleOpenDependencySettings}
      />
    );
  }, [
    orderedCanvasFormListGroups,
    dependencyFlags,
    getDependenciesForOption,
    handleOpenDependencySettings,
  ]);

  /**
   * renderGroupHeaderContent
   * ---------------------------------------------------------------------------
   * Form Lists-specific group header content for the shared Spreadsheet engine.
   *
   * RENDERED ORDER
   *  • field type icon
   *  • field label
   *  • SmartList/org-wide icon when the group is org-managed
   *
   * IMPORTANT
   *  • The shared Spreadsheet/ layer remains domain-free.
   *  • The icon and SmartList semantics stay in FormLists-specific UI.
   *  • This callback is intentionally stable so GroupedSpreadsheetSheet memo
   *    comparisons do not fail on every page render.
   */
  const renderGroupHeaderContent = useCallback(({ group, title }) => (
    <FormListsGroupHeaderContent
      group={group}
      label={title}
      variant="sheet"
    />
  ), []);

  /**
   * isCellReadOnly
   * ---------------------------------------------------------------------------
   * Form Lists governance boundary for the shared Spreadsheet engine.
   *
   * ENTERPRISE POLICY
   *  • SmartList/org-managed option values are centrally governed.
   *  • Therefore, Option and Hint cells are read-only in FormListsLab unless
   *    governance explicitly allows local option editing.
   *  • Dependency cells remain form-specific and must stay available for
   *    supported trigger options.
   *
   * IMPORTANT
   *  • The shared Spreadsheet/ engine remains domain-free.
   *  • The dependency column is already a custom read-only spreadsheet column.
   *    This page-level policy should not block its action behavior.
   *  • Returning true here prevents:
   *      - text edit mode
   *      - paste into governed value cells
   *      - Delete/Backspace clearing governed value cells
   */
  const isCellReadOnly = useCallback(({ group, column, columnKey }) => {
    /**
     * Keep this path intentionally quiet.
     *
     * WHY
     *  • The shared spreadsheet calls isCellReadOnly during rendering,
     *    keyboard handling, paste handling, and clear/delete handling.
     *  • Logging here can run once per visible SmartList value cell and create
     *    console noise or perceived UI lag.
     *
     * ENTERPRISE POLICY
     *  • SmartList value cells are visually and behaviorally read-only.
     *  • Dependency cells remain form-specific and available where supported.
     */
    return isFormListsValueCellReadOnly({
      group,
      column,
      columnKey,
    });
  }, []);

  /**
   * isColumnSortDisabled
   * ---------------------------------------------------------------------------
   * Form Lists governance boundary for sorting.
   *
   * WHY THIS EXISTS
   *  • Sorting reorders option rows.
   *  • Reordering is a form-local option edit because it changes saved option
   *    order when Save Draft / Publish later flushes dirty groups.
   *  • Org-managed SmartList option rows should not be locally reordered unless
   *    governance explicitly allows local option editing.
   *
   * IMPORTANT
   *  • This keeps the shared Spreadsheet engine domain-free.
   *  • The engine only asks whether sorting is disabled for this group/column.
   *  • Form Lists decides based on existing governance rules.
   */
  const isColumnSortDisabled = useCallback(({ group, column, columnKey }) => {
    if (columnKey === 'dependency') {
      return true;
    }

    return isFormListsValueCellReadOnly({
      group,
      column,
      columnKey,
    });
  }, []);

  /**
   * normalizeCommittedFormListRow
   * ---------------------------------------------------------------------------
   * Form Lists adapter hook for the shared spreadsheet engine.
   *
   * WHY THIS EXISTS
   *  • The shared Spreadsheet/ canvas owns local row state after a commit.
   *  • The canvas must remain domain-free and should not know about optionId.
   *  • Form Lists dependencies require a stable optionId.
   *  • Therefore, Form Lists supplies a committed-row normalizer that assigns
   *    optionId when a row becomes a meaningful dropdown option row.
   *
   * IMPORTANT
   *  • The generated optionId is NOT temporary.
   *  • It becomes the stable local identity for this option row.
   *  • Later autosave/manual apply preserves this optionId in dropdownOptions.
   */
  const normalizeCommittedFormListRow = useCallback((context = {}) => {
    return ensureFormListOptionRowIdentity(context);
  }, []);

  const handleSelectWorkspaceGroup = useCallback((group) => {
    const groupId = getGroupId(group);
    if (!groupId) return;

    setSelectedWorkspaceGroupId(groupId);

    /**
     * Future enhancement:
     * GroupedSpreadsheetCanvas can expose scrollToGroup(groupId) if we want
     * side-panel selection to horizontally scroll the sheet to that group.
     *
     * For now, this remains a lightweight workspace selection state and avoids
     * adding Form Lists-specific behavior to the shared spreadsheet engine.
     */
  }, []);

  const handleOpenWorkspaceGroupSettings = useCallback((group) => {
    const fieldId = normalizeString(group?.fieldId || group?.field?.id, '');

    if (!fieldId) {
      console.error(
        '[FormListsLabPage] Cannot open workspace group settings: missing fieldId.',
        { group }
      );
      return;
    }

    handleOpenFieldSettings({
      fieldId,
      initialTab: 'basic',
      settingsAccessMode: FIELD_SETTINGS_ACCESS_MODE.FULL,
      fieldDependencyData: {},
    });
  }, [handleOpenFieldSettings]);

  const handleWorkspacePanelRowReorder = useCallback(({
    sourceIndex,
    destinationIndex,
  }) => {
    if (
      !Number.isFinite(Number(sourceIndex)) ||
      !Number.isFinite(Number(destinationIndex)) ||
      sourceIndex === destinationIndex
    ) {
      return;
    }

    setFormListGroupOrder((prev) => {
      const currentOrder = normalizeGroupOrder({
        groups: orderedPanelFormListGroups,
        groupOrder: prev,
      });

      const nextOrder = [...currentOrder];
      const [movedGroupId] = nextOrder.splice(sourceIndex, 1);

      if (!movedGroupId) {
        return currentOrder;
      }

      nextOrder.splice(destinationIndex, 0, movedGroupId);

      console.log('[FormListsLabPage] Reordered Lists sidepanel groups:', {
        sourceIndex,
        destinationIndex,
        movedGroupId,
        nextOrder,
      });

      return nextOrder;
    });
  }, [orderedPanelFormListGroups]);


  const handleResetFromContext = useCallback(() => {
    if (!canvasRef.current) return;

    canvasRef.current.resetFromGroups(orderedCanvasFormListGroups);
    showSnackbar('info', 'Lists Lab reset from current form state.');
  }, [
    orderedCanvasFormListGroups,
    showSnackbar,
  ]);

  const handleGoToLayout = useCallback(() => {
    if (!urlFormId) {
      console.error(
        '[FormListsLabPage] Cannot navigate to Layout: missing formId.',
      );
      showSnackbar('error', 'No form ID is available for navigation.');
      return;
    }

    navigate(`/form-builder/${urlFormId}/layout`);
  }, [
    navigate,
    showSnackbar,
    urlFormId,
  ]);

  const formListsUnsavedWorkingCopyStatus = useMemo(() => {
    if (typeof getFormListsUnsavedWorkingCopyStatus !== 'function') {
      return {
        hasUnsavedChanges: false,
        sources: {},
      };
    }

    return getFormListsUnsavedWorkingCopyStatus({
      formId: urlFormId,
    });
  }, [
    getFormListsUnsavedWorkingCopyStatus,
    urlFormId,
  ]);

  const hasSpreadsheetLocalDirty = dirtySummary.dirtyGroupCount > 0;

  const hasFormListsUnsavedWorkingCopyChanges =
    formListsUnsavedWorkingCopyStatus?.hasUnsavedChanges === true;

  const hasDirtyChanges =
    hasSpreadsheetLocalDirty ||
    hasFormListsUnsavedWorkingCopyChanges;

  /**
   * Workspace settings readiness
   * ---------------------------------------------------------------------------
   * The sidepanel should not render settings mode until FieldEditContext has
   * the selected field ready.
   *
   * WHY
   *  • handleOpenFieldSettings requests settings mode.
   *  • selectFieldForEdit updates FieldEditContext asynchronously from React's
   *    rendering perspective.
   *  • During that transition, selectedFieldForEdit can still be null or can
   *    still point to a previous field.
   *  • Rendering SpreadsheetWorkspaceSidePanel in settings mode during that gap
   *    causes the fallback "Select a field to edit" flash.
   *
   * ENTERPRISE RULE
   *  • requested mode and rendered mode are intentionally separate.
   *  • The shell renders settings mode only when settings content is ready.
   *  • Until then, the list remains visible and stable.
   */
  const selectedFieldForEditId = useMemo(() => (
    normalizeString(
      selectedFieldForEdit?.id ||
        selectedFieldForEdit?.fieldId ||
        selectedFieldForEdit?.metadata?.id,
      ''
    )
  ), [selectedFieldForEdit]);

  const isWorkspaceSettingsReady = Boolean(
    workspacePanelMode === 'settings' &&
      selectedFieldId &&
      selectedFieldForEdit &&
      selectedFieldForEditId === selectedFieldId
  );

  const effectiveWorkspacePanelMode = isWorkspaceSettingsReady
    ? 'settings'
    : 'list';

  /**
   * FormListsLab side-panel sections
   * ---------------------------------------------------------------------------
   * The SpreadsheetWorkspaceSidePanel owns the SmartLists-style drag/drop shell.
   * This page provides the Form Lists-specific row content.
   *
   * IMPORTANT
   *  • Each item receives exactly one group.
   *  • FormListsLabSidePanelList is intentionally minimal: icon + label only.
   *  • Clicking a row opens the existing FieldSettings flow.
   */
  const formListsPanelSections = useMemo(() => {
    return [
      {
        title: 'Fields',
        noAccordion: true,
        noPadding: true,
        items: orderedPanelFormListGroups
          .map((group) => {
            const groupId = getGroupId(group);

            if (!group || !groupId) {
              return null;
            }

            return {
              localDraftId: groupId,
              element: (
                <FormListsLabSidePanelList
                  group={group}
                  selected={groupId === selectedWorkspaceGroupId}
                  onClick={(clickedGroup) => {
                    handleSelectWorkspaceGroup(clickedGroup);
                    handleOpenWorkspaceGroupSettings(clickedGroup);
                  }}
                />
              ),
            };
          })
          .filter(Boolean),
      },
    ];
  }, [
    orderedPanelFormListGroups,
    selectedWorkspaceGroupId,
    handleSelectWorkspaceGroup,
    handleOpenWorkspaceGroupSettings,
  ]);

  const fieldSettingsDropdownOptionsOverride = useMemo(() => {
    if (
      settingsAccessMode !== FIELD_SETTINGS_ACCESS_MODE.DEPENDENCIES_ONLY
    ) {
      return null;
    }

    return dependencyDropdownOptionsOverride || effectiveDropdownOptions;
  }, [
    dependencyDropdownOptionsOverride,
    effectiveDropdownOptions,
    settingsAccessMode,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flex: '1 1 auto',
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <SpreadsheetWorkspaceSidePanel
        mode={effectiveWorkspacePanelMode}
        title="Lists"
        sections={formListsPanelSections}
        settingsContent={
          isWorkspaceSettingsReady ? (
            <FieldSettings
              onSave={() => {}}
              onNavigateBack={handleCloseSettings}
              selectedField={selectedFieldForEdit}
              initialTab={initialTab}
              fieldDependencyData={fieldDependencyData}
              dropdownOptionsOverride={fieldSettingsDropdownOptionsOverride}
              settingsAccessMode={settingsAccessMode}
            />
          ) : null
        }
        width={FORM_LISTS_LAB_PANEL_EXPANDED_WIDTH}
        minWidth={FORM_LISTS_LAB_PANEL_EXPANDED_WIDTH}
        maxWidth={FORM_LISTS_LAB_PANEL_EXPANDED_WIDTH}
        collapsedWidth={FORM_LISTS_LAB_PANEL_COLLAPSED_WIDTH}
        isCollapsed={workspacePanelCollapsed}
        onToggleCollapse={(nextCollapsed) => {
          setWorkspacePanelCollapsed(nextCollapsed);
        }}
        dirtyBadgeVisible={hasDirtyChanges}
        hideTopBarInSettings
        onRowReorder={handleWorkspacePanelRowReorder}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          bgcolor: 'background.default',
        }}
      >

        <Box
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : loadError ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                px: 3,
              }}
            >
              <Alert severity="error">
                {loadError?.message || String(loadError)}
              </Alert>
            </Box>
          ) : !hasHydratedColumnWidths ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 280,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : hasNoFormListGroups ? (
            <Box
              sx={{
                height: '100%',
                minHeight: 320,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                width: '100%',
                px: 3,
              }}
            >
              <Stack
                spacing={1.25}
                alignItems="center"
                sx={{
                  maxWidth: 560,
                  mx: 'auto',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: 'text.disabled',
                    fontWeight: 700,
                  }}
                >
                  You have no Lists yet
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  Create at least one dropdown field (single select or
                  multi-select) to view the dropdowns and dependencies here.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleGoToLayout}
                  disabled={!urlFormId}
                  sx={{
                    mt: 0.5,
                  }}
                >
                  Go to Layout
                </Button>
              </Stack>
            </Box>
          ) : (
            <GroupedSpreadsheetCanvas
              ref={canvasRef}
              groups={orderedCanvasFormListGroups}
              columns={gridDefaults.columns}
              initialColumnWidths={initialFormListColumnWidths}
              groupIdKey={gridDefaults.groupIdKey}
              rowIdKey={gridDefaults.rowIdKey}
              rowKey={gridDefaults.rowKey}
              visibleRowCount={gridDefaults.visibleRowCount}
              rowAppendCount={gridDefaults.rowAppendCount}
              generatedRowPrefix={gridDefaults.generatedRowPrefix}
              onDirtyStateChange={handleDirtyStateChange}
              includeDirtyDiagnostics={false}
              showDirtyCellIndicator={false}
              onColumnWidthsChange={handleColumnWidthsChange}
              onCellCommit={handleCellCommit}
              onCommitBoundary={handleFormListsCommitBoundary}
              normalizeCommittedRow={normalizeCommittedFormListRow}
              renderCustomCell={renderCustomCell}
              renderGroupHeaderContent={renderGroupHeaderContent}
              isCellReadOnly={isCellReadOnly}
              isColumnSortDisabled={isColumnSortDisabled}
              sx={spreadsheetCanvasSx}
            />
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FormListsLabPage;